// Knowledge-base panel for the bot-config Conocimiento tab. Lists an agent's
// KB sources with live ingestion status (polls while anything is processing),
// uploads a file or a URL, approves a source (review → active, so the agent
// actually uses it), and deletes one (behind a confirm). Talks to the operator
// KB endpoints.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import {
  type KbApi,
  type KbChunk,
  type KbPreviewResult,
  type KbSource,
  type UnansweredQuestion,
} from './bot-config-data.js'
import { Button, Chip, Input, Modal, Spinner } from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'

const IN_PROGRESS = new Set(['queued', 'parsing', 'chunking', 'embedding'])

function statusChip(status: string): { label: string; tone: 'ok' | 'warn' | 'info' | 'sens' } {
  if (status === 'active') return { label: 'Activo', tone: 'ok' }
  if (status === 'review') return { label: 'Listo para aprobar', tone: 'warn' }
  if (status === 'failed' || status === 'rejected') return { label: 'Error', tone: 'sens' }
  if (IN_PROGRESS.has(status)) return { label: 'Procesando…', tone: 'info' }
  return { label: status, tone: 'info' }
}

function sourceName(s: KbSource): string {
  return s.filename ?? s.sourceUrl ?? `${s.kind}`
}

export function KbPanel({ scopeKey, api }: { scopeKey: string; api: KbApi }): JSX.Element {
  const qc = useQueryClient()
  const queryKey = ['kb', scopeKey]
  const { data: sources = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => api.list(),
    // Poll while any source is still being ingested.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((s) => IN_PROGRESS.has(s.status)) ? 3000 : false,
  })

  // "Preguntas sin respuesta": questions the agent recently couldn't answer.
  const { data: unanswered = [] } = useQuery({
    queryKey: ['kb-unanswered', scopeKey],
    queryFn: () => api.unanswered!(),
    enabled: api.unanswered !== undefined,
    // Always refetch when the panel opens: a question deferred seconds ago should
    // surface on the next open, not sit behind the default 10s stale window.
    staleTime: 0,
    refetchOnMount: 'always',
  })
  // Split real KB gaps (on-topic) from off-topic noise (#3): gaps are the
  // actionable worklist; off-topic is tucked away so it doesn't clutter it.
  const gaps = unanswered.filter((u) => u.relevance !== 'off_topic')
  const offTopic = unanswered.filter((u) => u.relevance === 'off_topic')

  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)
  // Per-source fragment viewer: which source is expanded + its loaded chunks.
  const [openId, setOpenId] = useState<string | null>(null)
  const [chunksByAsset, setChunksByAsset] = useState<Record<string, KbChunk[]>>({})
  // "Probar conocimiento": a retrieval probe.
  const [probe, setProbe] = useState('')
  const [probing, setProbing] = useState(false)
  const [probeResult, setProbeResult] = useState<KbPreviewResult | null>(null)
  // Delete is destructive (the agent stops using that source), so confirm first.
  const [deleteTarget, setDeleteTarget] = useState<KbSource | null>(null)

  async function runProbe(): Promise<void> {
    const q = probe.trim()
    if (q === '' || !api.preview) return
    setProbing(true)
    setError('')
    try {
      setProbeResult(await api.preview(q))
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo probar el conocimiento.'))
    } finally {
      setProbing(false)
    }
  }

  async function refresh(): Promise<void> {
    await qc.invalidateQueries({ queryKey })
  }

  async function run(fn: () => Promise<void>, fallback: string): Promise<void> {
    setBusy(true)
    setError('')
    try {
      await fn()
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, fallback))
    } finally {
      setBusy(false)
    }
  }

  async function submitUrl(): Promise<void> {
    const u = url.trim()
    if (u === '') return
    await run(async () => {
      await api.uploadUrl(u)
      setUrl('')
    }, 'No se pudo agregar la URL.')
  }

  // Bulk upload: send every selected file (sequentially so a failure is clear).
  async function submitFiles(): Promise<void> {
    const files = fileRef.current?.files
    if (!files || files.length === 0) return
    await run(async () => {
      for (const file of Array.from(files)) await api.uploadFile(file)
      if (fileRef.current) fileRef.current.value = ''
    }, 'No se pudo subir el archivo.')
  }

  async function confirmDelete(): Promise<void> {
    if (!deleteTarget) return
    const id = deleteTarget.id
    setDeleteTarget(null)
    await run(() => api.remove(id), 'No se pudo eliminar.')
  }

  async function toggleChunks(assetId: string): Promise<void> {
    if (openId === assetId) {
      setOpenId(null)
      return
    }
    setOpenId(assetId)
    if (!chunksByAsset[assetId] && api.chunks) {
      try {
        const c = await api.chunks(assetId)
        setChunksByAsset((m) => ({ ...m, [assetId]: c }))
      } catch (err) {
        setError(apiErrorMessage(err, 'No se pudieron cargar los fragmentos.'))
      }
    }
  }

  if (isLoading) return <Spinner label="Cargando fuentes…" />

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-xl border border-line bg-surface p-3.5">
        <p className="text-[12px] font-semibold text-text">Agregar conocimiento</p>
        <div className="flex flex-wrap items-end gap-2">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://… (página con información)"
            aria-label="URL de la fuente"
            className="min-w-[240px] flex-1"
          />
          <Button
            variant="secondary"
            onClick={() => void submitUrl()}
            disabled={busy || url.trim() === ''}
          >
            Agregar URL
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            multiple
            accept=".pdf,.txt,.md,.png,.jpg,.jpeg"
            aria-label="Archivos a subir"
            className="text-[12px] text-text-2 file:mr-3 file:rounded-lg file:border file:border-input-line file:bg-surface file:px-3 file:py-1.5 file:text-[12px] file:font-semibold file:text-text-2"
          />
          <Button variant="secondary" onClick={() => void submitFiles()} loading={busy}>
            Subir archivos
          </Button>
        </div>
        <p className="text-[11px] text-text-3">
          PDF, texto, Markdown o imagen (máx. 25 MB). Puedes elegir varios a la vez.
        </p>
        {error ? (
          <p className="text-[12px] text-sens" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      {api.preview ? (
        <div className="flex flex-col gap-3 rounded-xl border border-line bg-surface p-3.5">
          <p className="text-[12px] font-semibold text-text">Probar conocimiento</p>
          <p className="text-[11px] text-text-3">
            Escribe una pregunta como la haría un cliente y mira qué fragmentos encontraría el
            agente (con su puntaje). Útil para detectar huecos o información que no está cargada.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <Input
              value={probe}
              onChange={(e) => setProbe(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void runProbe()
              }}
              placeholder="p. ej. ¿cuál es el horario los sábados?"
              aria-label="Pregunta de prueba"
              className="min-w-[240px] flex-1"
            />
            <Button
              onClick={() => void runProbe()}
              loading={probing}
              disabled={probe.trim() === ''}
            >
              Probar
            </Button>
          </div>
          {probeResult ? (
            probeResult.degraded ? (
              // The probe could not RUN. Never fall through to the empty-result
              // copy below: it would tell the author to upload a document to fix
              // someone else's outage.
              <p className="text-[12px] text-text-2">{probeResult.degraded.message}</p>
            ) : probeResult.matches.length === 0 ? (
              <p className="text-[12px] text-text-2">
                No encontró nada relevante. El agente respondería que no tiene esa información (o
                pasaría con una persona). Sube un documento que cubra esto.
                {probeResult.excluded ? (
                  <span className="mt-1 block text-text-3">
                    Lo más cercano (por debajo del umbral,{' '}
                    {Math.round(probeResult.excluded.similarity * 100)}%):{' '}
                    {probeResult.excluded.content.slice(0, 160)}…
                  </span>
                ) : null}
              </p>
            ) : (
              <ol className="flex flex-col gap-1.5">
                {probeResult.matches.map((m) => (
                  <li
                    key={m.id}
                    className="rounded-md border border-line bg-surface-2 px-2.5 py-1.5"
                  >
                    <span className="text-[10px] font-semibold text-text-3">
                      {m.title || 'Fragmento'} · {Math.round(m.similarity * 100)}%
                    </span>
                    <p className="text-[12px] leading-relaxed text-text">
                      {m.content.slice(0, 260)}
                      {m.content.length > 260 ? '…' : ''}
                    </p>
                  </li>
                ))}
              </ol>
            )
          ) : null}
        </div>
      ) : null}

      {api.unanswered ? (
        <div className="flex flex-col gap-2 rounded-xl border border-line bg-surface p-3.5">
          <p className="text-[12px] font-semibold text-text">Preguntas sin respuesta</p>
          <p className="text-[11px] text-text-3">
            Aquí aparecen las preguntas, relacionadas con tu negocio, que el agente no pudo
            responder. Agrega lo que falte en <strong>Perfil del negocio</strong> o sube un
            documento aquí.
          </p>
          {gaps.length === 0 ? (
            <p className="text-[12px] text-text-2">
              Aún no hay ninguna. Cuando un cliente pregunte algo que el agente no sepa, aparecerá
              aquí.
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {gaps.map((u) => (
                <UnansweredRow key={u.question} item={u} />
              ))}
            </ul>
          )}

          {/* Off-topic questions: not gaps to fill (chit-chat, another industry).
              Tucked in a collapsed section so the worklist above stays actionable. */}
          {offTopic.length > 0 ? (
            <details className="mt-1 rounded-md border border-line bg-surface-2/40">
              <summary className="cursor-pointer list-none px-2.5 py-1.5 text-[11.5px] text-text-3 marker:hidden">
                Fuera de tema ({offTopic.length}) — preguntas que no parecen de tu negocio
              </summary>
              <ul className="flex flex-col gap-1.5 px-2.5 pb-2.5 opacity-70">
                {offTopic.map((u) => (
                  <UnansweredRow key={u.question} item={u} />
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}

      {sources.length === 0 ? (
        <p className="text-[12.5px] text-text-2">
          Aún no hay fuentes. Sube un documento o agrega una URL para que el agente pueda responder
          con esa información.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5" aria-label="Fuentes de conocimiento">
          {sources.map((s) => {
            const chip = statusChip(s.status)
            const canView = api.chunks !== undefined && s.chunks > 0
            const open = openId === s.id
            const frags = chunksByAsset[s.id]
            return (
              <li
                key={s.id}
                className="flex flex-col gap-2 rounded-xl border border-line bg-surface px-3 py-2.5"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-[12.5px] font-semibold text-text">
                      {sourceName(s)}
                    </span>
                    <span className="text-[11px] text-text-2">
                      {s.kind} · {s.chunks} fragmentos
                      {s.status === 'failed' && s.error ? ` · ${s.error}` : ''}
                    </span>
                  </span>
                  <span className="ml-auto flex flex-wrap items-center gap-2">
                    <Chip tone={chip.tone} dot>
                      {chip.label}
                    </Chip>
                    {canView ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void toggleChunks(s.id)}
                        aria-expanded={open}
                      >
                        {open ? 'Ocultar' : 'Ver fragmentos'}
                      </Button>
                    ) : null}
                    {s.status === 'review' ? (
                      <Button
                        size="sm"
                        onClick={() => void run(() => api.approve(s.id), 'No se pudo aprobar.')}
                        disabled={busy}
                      >
                        Aprobar
                      </Button>
                    ) : null}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteTarget(s)}
                      disabled={busy}
                    >
                      Eliminar
                    </Button>
                  </span>
                </div>
                {open ? (
                  <div className="flex flex-col gap-1.5 rounded-lg bg-surface-2 p-2.5">
                    <p className="text-[11px] text-text-3">
                      Así quedó dividido el documento. Si un fragmento se ve cortado o sin sentido,
                      conviene reformular el documento.
                    </p>
                    {frags === undefined ? (
                      <Spinner label="Cargando fragmentos…" />
                    ) : frags.length === 0 ? (
                      <p className="text-[12px] text-text-2">Sin fragmentos activos.</p>
                    ) : (
                      <ol className="flex flex-col gap-1.5">
                        {frags.map((f, i) => (
                          <li
                            key={f.id}
                            className="rounded-md border border-line bg-surface px-2.5 py-1.5"
                          >
                            <span className="text-[10px] font-semibold text-text-3">
                              Fragmento {i + 1}
                            </span>
                            <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-text">
                              {f.content}
                            </p>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}

      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="¿Eliminar esta fuente de conocimiento?"
        subtitle={
          deleteTarget
            ? `Se eliminará "${sourceName(deleteTarget)}". El agente dejará de usar esta información.`
            : ''
        }
        role="alertdialog"
        tone="sens"
        busy={busy}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => void confirmDelete()} loading={busy}>
              Sí, eliminar
            </Button>
          </>
        }
      >
        <p className="text-[12.5px] text-text-2">Esta acción no se puede deshacer.</p>
      </Modal>
    </div>
  )
}

/** One row in the "Preguntas sin respuesta" list: the question + an "N×" badge
 *  when it was asked more than once. Shared by the gaps + off-topic lists. */
function UnansweredRow({ item }: { item: UnansweredQuestion }): JSX.Element {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 rounded-md border border-line bg-surface-2 px-2.5 py-1.5">
      <span className="text-[12.5px] text-text">{item.question}</span>
      {item.times > 1 ? (
        <span className="rounded-pill border border-line bg-hover px-1.5 text-[10px] font-bold text-text-3">
          {item.times}×
        </span>
      ) : null}
    </li>
  )
}
